// React & React ecosystem
import { useNavigate } from 'react-router-dom';

// External libraries (alphabetically)
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Internal absolute imports (@/ aliases)
// Stores
import { useAuthStore } from '@/store/authStore';

// Relative imports from same feature
import { authService } from '../services/authService';

/**
 * DOCU: React Query mutation hook for user logout and session cleanup. <br>
 * Triggered: Called when user clicks logout button in navigation. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @returns Mutation object with logout function that clears auth state and cache.
 * @author Kirt
 *
 * @example
 * ```tsx
 * const { mutate: logout } = useLogout();
 *
 * const handleLogout = () => {
 *   logout();
 * };
 * ```
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = useAuthStore((state) => state.logout);

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Clear auth state
      logout();

      // Clear all queries
      queryClient.clear();

      toast.success('Logged out successfully');

      // Navigate to login
      navigate('/login');
    },
    onError: () => {
      // Still logout locally even if server request fails
      logout();
      queryClient.clear();
      navigate('/login');
    },
  });
};
